/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';

import { useMemo } from 'react';

import { useRef } from 'react';

function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;

  return ref;
}

const useUnmount = (fn: () => void) => {
  const fnRef = useLatest(fn);
  useEffect(
    () => () => {
      fnRef.current();
    },
    []
  );
};

type noop = (...args: any[]) => any;

function useDebounceFn<T extends noop>(fn: T, wait = 1000) {
  const fnRef = useLatest(fn);

  const debounced = useMemo(
    () =>
      debounce((...args: Parameters<T>): ReturnType<T> => {
        return fnRef.current(...args);
      }, wait),
    []
  );

  useUnmount(() => {
    debounced.cancel();
  });

  return {
    run: debounced,
    cancel: debounced.cancel,
    flush: debounced.flush,
  };
}

export default useDebounceFn;

export function useDebouncedValue<T>(defaultValue: T, time = 500): [T, (v: T) => void, T] {
  const [value, setValue] = useState(defaultValue);
  const [debounced, setDebounced] = useState(defaultValue);

  const { run } = useDebounceFn(() => {
    setDebounced(value);
  }, time);

  useEffect(() => {
    run();
  }, [value]);

  return [value, setValue, debounced];
}
